import {
  bandOf,
  LEGACY_RISK_MATRIX,
  type RaMatrixDefinition,
  type ReauthKind,
  type RiskLevel,
} from '@felix/domain';
import { Button, Modal, SegmentedControl, Textarea } from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowRight,
  IconBolt,
  IconCircleCheck,
  IconFlame,
  IconFlask,
  IconInfoCircle,
  IconLadder,
  IconLock,
  IconMoodSick,
  IconPackage,
  IconSettings,
  IconShieldCheck,
  IconShip,
  IconWind,
  IconZzz,
} from '@tabler/icons-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReauthField, useReauth } from '../../approval-policy.js';
import { Panel } from '../../Panel.js';
import { StatTile } from '../../StatTile.js';
import { StatusBadge } from '../../StatusBadge.js';
import classes from './Risk.module.css';
import type { RiskLibraryEntry, RiskStatus, RiskSummary } from './types.js';
import { levelTone, type MatrixMode, matrixCounts, riskKpis, statusTone } from './view.js';

/** Hata kodunun metni (`risk.errors.<kod>`; bilinmeyen kod genel metin ve kodla). */
export function useRiskError() {
  const { t } = useTranslation();
  return (code: string) =>
    t(`risk.errors.${code}`, { defaultValue: '' }) || `${t('risk.errors.GENERIC')} (${code})`;
}

export function LevelBadge({ level, score }: { level: RiskLevel; score?: number }) {
  const { t } = useTranslation();
  return (
    <StatusBadge tone={levelTone(level)} withIcon={false}>
      {t(`risk.levels.${level}`)}
      {score !== undefined ? ` (${score})` : ''}
    </StatusBadge>
  );
}

export function RiskStatusBadge({
  item,
}: {
  item: Pick<RiskSummary, 'status' | 'pending' | 'reviewState' | 'changed'>;
}) {
  const { t } = useTranslation();
  const status: RiskStatus = item.status;
  return (
    <span className={classes.statusCell}>
      <StatusBadge tone={statusTone(status)} withIcon={false}>
        {t(`risk.statuses.${status}`)}
        {item.pending?.office ? ` · ${t('risk.list.office')}` : ''}
      </StatusBadge>
      {item.reviewState === 'overdue' || item.reviewState === 'due' ? (
        <StatusBadge tone={item.reviewState === 'overdue' ? 'danger' : 'warning'} withIcon={false}>
          {t(`risk.review.${item.reviewState}`)}
        </StatusBadge>
      ) : null}
      {item.changed && status !== 'pending' ? (
        <StatusBadge tone="info" withIcon={false} variant="dot">
          {t('risk.list.changed')}
        </StatusBadge>
      ) : null}
    </span>
  );
}

/** Özet kartları (referans: Toplam, Yüksek, Orta, Düşük Risk). */
export function RiskKpis({ items }: { items: readonly RiskSummary[] }) {
  const { t } = useTranslation();
  const k = riskKpis(items);
  const tile = (
    icon: ReactNode,
    label: string,
    value: number,
    note: string,
    tone: 'danger' | 'warning' | 'default' | 'success',
  ) => (
    <div>
      <StatTile icon={icon} label={label} value={value} tone={tone} />
      <div className={classes.kpiNote}>{note}</div>
    </div>
  );
  return (
    <section className={classes.kpis} aria-label={t('risk.title')}>
      {tile(
        <IconAlertTriangle size={20} aria-hidden />,
        t('risk.kpi.total'),
        k.total,
        t('risk.kpi.totalNote'),
        'danger',
      )}
      {tile(
        <IconBolt size={20} aria-hidden />,
        t('risk.kpi.high'),
        k.high,
        t('risk.kpi.highNote'),
        'warning',
      )}
      {tile(
        <IconInfoCircle size={20} aria-hidden />,
        t('risk.kpi.medium'),
        k.medium,
        t('risk.kpi.mediumNote'),
        'default',
      )}
      {tile(
        <IconCircleCheck size={20} aria-hidden />,
        t('risk.kpi.low'),
        k.low,
        t('risk.kpi.lowNote'),
        'success',
      )}
    </section>
  );
}

/**
 * 1. Risk Matrix: olasılık 5→1 satırlar, etki 1→5 sütunlar; hücre sayısı ve seviye rengi. Renk
 * ve lejant şirketin geçerli matris sürümünden (ADR 0085 §2); `label` profil adı ve sürümü.
 */
export function RiskMatrix({
  items,
  mode,
  onMode,
  selected,
  onCell,
  matrix = LEGACY_RISK_MATRIX,
  label,
}: {
  matrix?: RaMatrixDefinition;
  label?: string;
  items: readonly RiskSummary[];
  mode: MatrixMode;
  onMode: (mode: MatrixMode) => void;
  selected: { likelihood: number; severity: number } | null;
  onCell: (likelihood: number, severity: number) => void;
}) {
  const { t } = useTranslation();
  const grid = matrixCounts(items, mode);
  const rows = [5, 4, 3, 2, 1];
  const cols = [1, 2, 3, 4, 5];
  return (
    <Panel title={t('risk.matrix.title')}>
      <SegmentedControl
        className={classes.modeSwitch}
        size="xs"
        aria-label={t('risk.matrix.mode')}
        value={mode}
        onChange={(value) => onMode(value as MatrixMode)}
        data={[
          { value: 'initial', label: t('risk.matrix.initial') },
          { value: 'residual', label: t('risk.matrix.residual') },
        ]}
      />
      <div className={classes.matrixWrap}>
        <span className={classes.axisY}>{t('risk.matrix.likelihood')}</span>
        <div className={classes.matrix}>
          {rows.map((l) => [
            <span key={`r${l}`} className={classes.rowLabel}>
              <span>{l}</span>
              <span>{t(`risk.scale.${l}`)}</span>
            </span>,
            ...cols.map((s) => {
              const count = grid[l]?.[s] ?? 0;
              const level = bandOf(matrix, l * s).key;
              const active = selected?.likelihood === l && selected.severity === s;
              return (
                <button
                  key={`${l}-${s}`}
                  type="button"
                  className={classes.cell}
                  data-level={level}
                  data-active={active || undefined}
                  data-empty={count === 0 || undefined}
                  aria-pressed={active}
                  aria-label={t('risk.matrix.cell', { likelihood: l, severity: s, count })}
                  onClick={() => onCell(l, s)}
                >
                  {count}
                </button>
              );
            }),
          ])}
          <span />
          {cols.map((s) => (
            <span key={`c${s}`} className={classes.colLabel}>
              <span>{s}</span>
              <span>{t(`risk.scale.${s}`)}</span>
            </span>
          ))}
          <span />
          <span className={classes.axisX}>{t('risk.matrix.impact')}</span>
        </div>
        <ul className={classes.legend}>
          {[...matrix.bands].reverse().map((band) => (
            <li key={band.key}>
              <span className={classes.swatch} data-level={band.key} aria-hidden />
              <span>
                {t(`risk.levels.${band.key}`)}
                <span className={classes.legendRange}>
                  ({band.min}–{band.max})
                </span>
              </span>
            </li>
          ))}
        </ul>
      </div>
      {label ? <p className={classes.matrixProfile}>{label}</p> : null}
    </Panel>
  );
}

/** 5. Risk Scoring Guide. */
export function ScoringGuide() {
  const { t } = useTranslation();
  const scale = [1, 2, 3, 4, 5];
  return (
    <Panel title={t('risk.guide.title')}>
      <div className={classes.guide}>
        <section>
          <h3>{t('risk.guide.impact')}</h3>
          <dl>
            {scale.map((n) => [
              <dt key={`s${n}`}>
                {n} – {t(`risk.scale.${n}`)}
              </dt>,
              <dd key={`sd${n}`}>{t(`risk.guide.severity.${n}`)}</dd>,
            ])}
          </dl>
        </section>
        <section>
          <h3>{t('risk.guide.likelihood')}</h3>
          <dl>
            {scale.map((n) => [
              <dt key={`l${n}`}>
                {n} – {t(`risk.scale.${n}`)}
              </dt>,
              <dd key={`ld${n}`}>{t(`risk.guide.probability.${n}`)}</dd>,
            ])}
          </dl>
        </section>
      </div>
    </Panel>
  );
}

const CATEGORY_ICONS: Record<string, typeof IconShip> = {
  mooring: IconShip,
  navigation: IconWind,
  cargo: IconPackage,
  fire: IconFlame,
  chemical: IconFlask,
  work_at_height: IconLadder,
  enclosed_space: IconLock,
  hot_work: IconFlame,
  equipment: IconSettings,
  fatigue: IconZzz,
  security: IconShieldCheck,
  other: IconMoodSick,
};

/** Başlıktan senaryo ikonu (kütüphane sürümünde kategori yok). */
const ICON_HINTS: ReadonlyArray<readonly [RegExp, string]> = [
  [/moor|berth|anchor/i, 'mooring'],
  [/hot work|weld|fire/i, 'fire'],
  [/enclosed|confined|tank entry/i, 'enclosed_space'],
  [/height|aloft|over ?side/i, 'work_at_height'],
  [/cargo|crane|lift/i, 'cargo'],
  [/chemical|bunker|fuel/i, 'chemical'],
  [/navigat|pilot|passage|weather/i, 'navigation'],
  [/fatigue|watch/i, 'fatigue'],
  [/security|cyber|piracy/i, 'security'],
  [/engine|machinery|equipment|electric/i, 'equipment'],
];

function scenarioIcon(title: string): typeof IconShip {
  const hit = ICON_HINTS.find(([pattern]) => pattern.test(title));
  return (hit ? CATEGORY_ICONS[hit[1]] : undefined) ?? IconShip;
}

/**
 * 6. Risk Scenarios: şirket RA kütüphanesinin yayımlı sürümleri (K3; ADR 0087 §5). Gemide bu
 * gemiye atanmış güncel sürümden değerlendirme açılır; atanmamış ya da eski sürüm kullanılamaz.
 * Ofiste salt okunur liste (yazma Risk ▸ Library'de).
 */
export function Scenarios({
  state,
  onUse,
}: {
  state: { kind: 'loading' } | { kind: 'error' } | { kind: 'ready'; list: RiskLibraryEntry[] };
  onUse?: ((entry: RiskLibraryEntry) => void) | undefined;
}) {
  const { t } = useTranslation();
  const list = state.kind === 'ready' ? state.list : null;
  return (
    <Panel title={t('risk.scenarios.title')}>
      <p className={classes.panelNote}>{t('risk.scenarios.note')}</p>
      {state.kind === 'error' ? (
        <p className={classes.error}>{t('risk.scenarios.error')}</p>
      ) : !list ? null : list.length === 0 ? (
        <p className={classes.panelNote}>{t('risk.scenarios.empty')}</p>
      ) : (
        <div className={classes.scenarios}>
          {list.map((entry) => {
            const Icon = scenarioIcon(entry.title);
            const usable = onUse !== undefined && entry.current;
            const body = (
              <>
                <span className={classes.scenarioHead}>
                  <span className={classes.scenarioIcon}>
                    <Icon size={16} aria-hidden />
                  </span>
                  {entry.title}
                </span>
                <span className={classes.scenarioText}>
                  {entry.code} {entry.revision}
                  {entry.scope ? ` · ${entry.scope}` : ''}
                </span>
                {entry.inherent ? (
                  <LevelBadge level={entry.inherent.level} score={entry.inherent.score} />
                ) : null}
                {onUse && !entry.current ? (
                  <StatusBadge tone="neutral">
                    {t(`risk.scenarios.${entry.reason ?? 'not_assigned'}`)}
                  </StatusBadge>
                ) : null}
              </>
            );
            return usable ? (
              <button
                key={entry.id}
                type="button"
                className={classes.scenario}
                aria-label={t('risk.scenarios.use', { title: entry.title })}
                onClick={() => onUse(entry)}
              >
                {body}
              </button>
            ) : (
              <div key={entry.id} className={classes.scenario}>
                {body}
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
}

/** 7. Karar ağacı ve değerlendirme sıklığı (referans bölüm 7; COSWP beş adım). */
export function DecisionTree() {
  const { t } = useTranslation();
  const arrow = (label?: string) => (
    <span className={classes.arrow} aria-hidden>
      {label ? <span>{label}</span> : null}
      <IconArrowRight size={18} />
    </span>
  );
  return (
    <Panel title={t('risk.tree.title')}>
      <ol
        className={classes.tree}
        aria-label={t('risk.tree.title')}
        style={{ listStyle: 'none', margin: 0, padding: 0 }}
      >
        <li className={classes.node} data-kind="start">
          {t('risk.tree.identified')}
        </li>
        {arrow(t('risk.tree.yes'))}
        <li className={classes.node}>{t('risk.tree.assess')}</li>
        {arrow()}
        <li className={classes.node} data-kind="decision">
          {t('risk.tree.high')}
        </li>
        {arrow(t('risk.tree.yes'))}
        <li className={classes.node} data-kind="plan">
          {t('risk.tree.plan')}
        </li>
      </ol>
      <div className={classes.monitorRow}>
        <span>
          {t('risk.tree.no')} <IconArrowRight size={14} aria-hidden />
        </span>
        <span className={classes.node}>{t('risk.tree.monitor')}</span>
      </div>
      <div className={classes.frequency}>
        <strong>
          <IconInfoCircle size={14} aria-hidden /> {t('risk.tree.frequency')}
        </strong>
        <ul>
          {(['before', 'yearly', 'change', 'incident'] as const).map((key) => (
            <li key={key}>{t(`risk.tree.when.${key}`)}</li>
          ))}
        </ul>
      </div>
    </Panel>
  );
}

/**
 * Gerekçe ve parola penceresi: gönderim, onay, iade, geri çekme, vazgeçme. Risk değerlendirmesinde
 * parola isteğe bağlıdır (ADR 0056): alan yerine bilgi metni; şirket ayarı açıksa ya da sunucu
 * isterse alan görünür. Parola yalnız girildiyse dolu gelir.
 */
export function ActionDialog({
  opened,
  title,
  body,
  reason,
  password,
  confirmLabel,
  danger,
  onClose,
  onConfirm,
  reauthKind = 'risk_assessment',
  messageOf,
  note,
  extra,
  extraBlocked = false,
}: {
  opened: boolean;
  title: string;
  body: string;
  reason: 'required' | 'optional' | 'none';
  password: boolean;
  confirmLabel: string;
  danger?: boolean;
  onClose: () => void;
  onConfirm: (input: { reason: string; password: string }) => Promise<string | null>;
  /** Parola kuralının türü (ADR 0075); şirket RA kütüphanesi `ra_library` (ADR 0087 §3). */
  reauthKind?: ReauthKind;
  /** Hata kodunun metni; verilmezse risk değerlendirmesi metinleri. */
  messageOf?: (code: string) => string;
  /** Gövdenin altında ek bilgi (ör. kendi onayı). */
  note?: string | null;
  /** Gerekçeden önce ek alanlar (ör. yetkinin geçerliliği; ADR 0089 §4). */
  extra?: ReactNode;
  /** Ek alan eksikse onay kapalı. */
  extraBlocked?: boolean;
}) {
  const { t } = useTranslation();
  const riskMessage = useRiskError();
  const message = messageOf ?? riskMessage;
  const [text, setText] = useState('');
  const reauth = useReauth(reauthKind);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const close = () => {
    setText('');
    reauth.reset();
    setError(null);
    onClose();
  };
  const blocked =
    extraBlocked ||
    (reason === 'required' && text.trim().length < 5) ||
    (password && reauth.missing);
  return (
    <Modal
      opened={opened}
      onClose={close}
      title={title}
      closeButtonProps={{ 'aria-label': t('risk.dialog.cancel') }}
    >
      <form
        className={classes.dialogBody}
        onSubmit={async (event) => {
          event.preventDefault();
          if (blocked) return;
          setBusy(true);
          const code = await onConfirm({
            reason: text.trim(),
            password: password ? (reauth.body().password ?? '') : '',
          });
          setBusy(false);
          if (code) {
            reauth.noteError({ code });
            setError(code);
          } else close();
        }}
      >
        <p style={{ margin: 0 }}>{body}</p>
        {note ? <p className={classes.panelNote}>{note}</p> : null}
        {extra}
        {reason !== 'none' ? (
          <Textarea
            label={t('risk.dialog.reason')}
            required={reason === 'required'}
            value={text}
            maxLength={2000}
            autosize
            minRows={2}
            onChange={(event) => setText(event.currentTarget.value)}
          />
        ) : null}
        {password ? <ReauthField reauth={reauth} label={t('risk.dialog.password')} mb="0" /> : null}
        {error ? (
          <p className={classes.error} role="alert">
            {message(error)}
          </p>
        ) : null}
        <div className={classes.formActions}>
          <Button
            type="submit"
            loading={busy}
            disabled={blocked}
            {...(danger ? { color: 'red' } : {})}
          >
            {confirmLabel}
          </Button>
          <Button variant="default" onClick={close}>
            {t('risk.dialog.cancel')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
